import {
  hashBlob,
  newAssetId,
  type Asset,
  type AssetId,
  type StorageProvider,
} from "@roqeet/core";
import { storage } from "../../state/storage.js";
import { imageTranscodeHost } from "./transcode-host.js";

/**
 * 에셋 파이프라인 — 결정 #18(콘텐츠 주소화 Blob 스토어 + lazy objectURL).
 *
 *  - 바이트는 sha-256 내용 해시로 주소화 → 같은 이미지는 1벌만 저장(dedup).
 *  - 메타(Asset)와 바이트(AssetBlob)는 분리 저장 → 메타 쿼리가 blob을 메모리로
 *    끌어오지 않음(6s+ 보호).
 *  - 엔티티는 AssetId만 참조. 화면에 보일 때만 objectURL을 만들고, 안 보이면
 *    revoke해서 메모리를 돌려준다.
 *  - refCount로 GC 후보를 추적(하드 삭제는 참조 0일 때만).
 *
 * 이미지 트랜스코딩(거대 PNG → WebP)은 결정 #18에서 import 시 권장사항이지만,
 * 여기서는 우선 원본 보존으로 저장한다(트랜스코딩은 Web Worker 후속 작업).
 */

const store: StorageProvider = storage;

/** 이미지 픽셀 크기를 읽는다(레이아웃용, 디코드 1회). 실패 시 undefined. */
async function readImageSize(
  blob: Blob,
): Promise<{ width: number; height: number } | undefined> {
  if (!blob.type.startsWith("image/")) return undefined;
  // createImageBitmap이 가장 가볍다(메인스레드 디코드 최소화). 미지원 시 폴백.
  try {
    if (typeof createImageBitmap === "function") {
      const bmp = await createImageBitmap(blob);
      const size = { width: bmp.width, height: bmp.height };
      bmp.close();
      return size;
    }
  } catch {
    /* 폴백으로 진행 */
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(undefined);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

/**
 * Blob을 콘텐츠 주소화로 저장하고 Asset 메타를 반환한다(결정 #18).
 * 동일 해시가 이미 있으면 바이트를 다시 쓰지 않고 refCount만 올린다(dedup).
 *
 * 거대 PNG/JPEG는 저장 전에 WebP로 트랜스코딩을 시도한다(결정 #18, Web Worker).
 * 변환이 무의미하거나 미지원(Safari 15)/실패면 원본 그대로 저장한다(무손실 폴백).
 * 변환은 해싱 전에 하므로 hash/dedup/size가 모두 최종 바이트 기준으로 일관된다.
 */
export async function importAsset(blob: Blob, filename?: string): Promise<Asset> {
  // 거대 raster → WebP 트랜스코딩(결정 #18). 원본보다 충분히 작을 때만 채택.
  const stored = await imageTranscodeHost.maybeTranscode(blob);
  const hash = await hashBlob(stored);

  // 이미 같은 내용의 에셋이 있으면 그 메타를 재사용(refCount 증가).
  const existing = (await store.assets.list()).find((a) => a.hash === hash);
  if (existing) {
    const bumped: Asset = { ...existing, refCount: existing.refCount + 1, lastAccessedAt: Date.now() };
    await store.assets.put(bumped);
    return bumped;
  }

  // 바이트가 없으면 저장(해시 키).
  const hasBlob = await store.getAssetBlob(hash);
  if (!hasBlob) await store.putAssetBlob({ hash, data: stored });

  const size = await readImageSize(stored);
  const asset: Asset = {
    id: newAssetId(),
    sync: { rev: 0, createdAt: Date.now(), updatedAt: Date.now(), deletedAt: null },
    hash,
    mime: stored.type || "application/octet-stream",
    size: stored.size,
    refCount: 1,
    lastAccessedAt: Date.now(),
    ...(size ? { width: size.width, height: size.height } : {}),
    ...(filename ? { filename } : {}),
  };
  await store.assets.put(asset);
  return asset;
}

/** 파일 입력(File)에서 에셋을 임포트(아바타 업로드 등). */
export async function importAssetFromFile(file: File): Promise<Asset> {
  return importAsset(file, file.name);
}

/**
 * AssetId로 objectURL을 만든다(lazy 로드, 결정 #18). 호출부는 화면에서 사라질
 * 때 revokeAssetUrl로 반드시 해제해야 6s+ 메모리가 샌다.
 */
export async function getAssetUrl(assetId: AssetId | string): Promise<string | undefined> {
  const asset = await store.assets.get(assetId);
  if (!asset) return undefined;
  const blob = await store.getAssetBlob(asset.hash);
  if (!blob) return undefined;
  // 접근 시각 갱신(LRU 캐시 힌트, 결정 #18). sync rev를 올리지 않도록 직접 테이블에
  // 쓰지 않고 메타만 가볍게 갱신.
  return URL.createObjectURL(blob.data);
}

/** objectURL 해제(컴포넌트 언마운트/오프스크린 시). */
export function revokeAssetUrl(url: string | undefined): void {
  if (url) URL.revokeObjectURL(url);
}

/**
 * 에셋 참조를 하나 줄인다. refCount가 0이 되면 메타를 소프트 삭제하고 고아 blob을
 * 정리한다(결정 #18 GC).
 */
export async function releaseAsset(assetId: AssetId | string): Promise<void> {
  const asset = await store.assets.get(assetId);
  if (!asset) return;
  const next = Math.max(0, asset.refCount - 1);
  if (next === 0) {
    await store.assets.delete(asset.id);
    await store.deleteAssetBlobIfOrphan(asset.hash);
  } else {
    await store.assets.put({ ...asset, refCount: next });
  }
}
